// File: Alert.jsx

import React from "react";
import PropTypes from "prop-types";
import { trim, template, values } from "lodash";

import LoadingState from "@/components/items-list/components/LoadingState";
import routeWithUserSession from "@/components/application-area/routeWithUserSession";
import navigateTo from "@/components/application-area/navigateTo";

import { currentUser } from "@/services/auth";
import notification from "@/services/notification";

import  AlertEventService  from "@/services/alert-events";
import routes from "@/services/routes";

import MenuButton from "../../components/alert-events/MenuButton";
import AlertEventView from "../../components/alert-events/AlertEventView";

const MODES = {
  VIEW: 1,
};

const defaultNameBuilder = template("<%= alert.name %>: <%= created_at %> <%= destination.name %> [<%= id %>]");

export function getDefaultName(alertevent) {
  if (!alertevent.id) {
    return "Unknown Event";
  }
  return defaultNameBuilder(alertevent);
}

class AlertEvent extends React.Component {
  static propTypes = {
    mode: PropTypes.oneOf(values(MODES)),
    alertEventId: PropTypes.string,
    onError: PropTypes.func,
  };

  static defaultProps = {
    mode: null,
    alertEventId: null,
    onError: () => {},
  };

  _isMounted = false;

  state = {
    alert_event: null,
    mode: null,
  };

  componentDidMount() {
    this._isMounted = true;
    const { mode, alertEventId } = this.props; // Get the alertId from props
    this.setState({ mode });
      AlertEventService.get({ id: alertEventId }) // Use alertId to fetch alert details
        .then(alert_event => {
          if (this._isMounted) {
            const canEdit = currentUser.canEdit(alert_event);
            this.setState({ alert_event,canEdit, is_favorite: alert_event.is_favorite , tags: alert_event.tags});
          }
        })
        .catch(error => {
          if (this._isMounted) {
            this.props.onError(error);
          }
        });
    
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  save = () => {
    const { alert_event } = this.state;
    alert_event.id = trim(alert_event.id) || getDefaultName(alert_event);

    return AlertEventService.save(alert_event)
      .then(alert_event => {
        notification.success("Saved.");
        this.setState({ alert_event, mode: MODES.VIEW });
        navigateTo(`alert_events/${alert_event.id}/view`, true);
      })
      .catch(() => {
        notification.error("Failed saving event.");
      });
  };

  setAlertEventOptions = (obj) => {
    const { alert_event } = this.state;
    const options = { ...alert_event.options, ...obj };
    this.setState({
      alert_event: Object.assign(alert_event, { options }),
    });
  };


  delete = () => {
    const { alert_event } = this.state;
    return AlertEventService.delete(alert_event.id)
      .then(() => {
        notification.success("Event deleted successfully.");
        navigateTo("alert_events");
      })
      .catch(() => {
        notification.error("Failed deleting event.");
      });
  };

  archive = () => {
    const { alert_event } = this.state;
    alert_event.is_archived = true;
    return AlertEventService.save(alert_event)
      .then(() => {
        this.setState({ alert_event });
        notification.success("Event archived successfully.");
        navigateTo(`alert_events/archive`);
      })
      .catch(() => {
        notification.error("Failed archiving event.");
      });
  };

  unarchive = () => {
    const { alert_event } = this.state;
    alert_event.is_archived = false;
    return AlertEventService.save(alert_event)
      .then(() => {
        this.setState({ alert_event });
        notification.success("Alert Event unarchived successfully.");
        navigateTo(`alert_events/${alert_event.id}/view`, true);
        this.setState({ mode: MODES.VIEW});
      })
      .catch(() => {
        notification.error("Failed unarchiving alert event.");
      });
  };

  cancel = () => {
    const { id } = this.state.alert_event;
    navigateTo(`alert_events/${id}/view`, true);
    this.setState({ mode: MODES.VIEW});
  };

  favorite = () => {
    const { id } = this.state.alert_event;
    return AlertEventService.favorite(id)
      .then(() => {
        notification.success("Alert Event added to favorites.");
      })
      .catch(() => {
        notification.error("Failed adding alert event to favorites.");
      });
  };

  unfavorite = () => {
    const { id } = this.state.alert_event;
    return AlertEventService.unfavorite(id)
      .then(() => {
        //this.unfavorite()
        notification.success("Alert Event removed from favorites.");
      })
      .catch(() => {
        notification.error("Failed removing alert event from favorites.");
      });
  };

  setAlertEventTags = obj => {
    if (!Array.isArray(obj)) {
        console.error("Expected an array for tags, but received:", obj);
        return;
    }

    this.setState(prevState => ({
        alert_event: { ...prevState.alert_event, tags: obj }
    }), this.save); // Ensure save is called after state update
};

  render() {
    const { alert_event } = this.state;
    if (!alert_event) {
      return <LoadingState className="m-t-30" />;
    }

    const { mode, canEdit } = this.state;

    const menuButton = (
      <MenuButton 
        doDelete={this.delete} 
        doArchive={this.archive} 
        canEdit={canEdit} 

        doUnarchive={this.unarchive} 
        archived={alert_event.is_archived} 
        />
    );
    
    const enhancedAlertEvent = {
      ...alert_event,
      favorite: this.favorite,
      unfavorite: this.unfavorite,
      is_favorite: alert_event.is_favorite,
      tags: alert_event.tags,
      query: alert_event.query,
      alert: alert_event.alert,
      destination: alert_event.destination,
    };

    const commonProps = {
      alert_event: enhancedAlertEvent,
      save: this.save,
      setAlertEventTags: this.setAlertEventTags,
      menuButton,
      onChange: () => {},
      name: getDefaultName(alert_event),
    };

    return (
      <div className="alert-page">
        <div className="container">
          {mode === MODES.VIEW && (
            <AlertEventView canEdit={canEdit}   {...commonProps} />
          )}
        </div>
      </div>
    );
  }
}


routes.register(
  "AlertEvents.View",
  routeWithUserSession({
    path: "/alert_events/:alertEventId",
    title: "Alert Event",
    render: pageProps => <AlertEvent {...pageProps} mode={MODES.VIEW} />,
  })
);